import { Component, Inject } from '@angular/core'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { ComponentContainer, JsonValue } from 'golden-layout'
import { SmartSuspenseKey } from '../panel-key'
import { Subject } from 'rxjs'
import {
  FilterGroupService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  MyAccountInfo,
  openCommentaryDrawer,
  RootState,
  selectFocusedMyAccount,
  selectUserPolicy,
  userHasCommentaryAccess,
} from '@helios/state-management'
import {
  buildEntityDescriptions,
  CommentaryEntityRef,
  CommentaryEntitySearchService,
  CommentaryFollowService,
  CommentaryGridSelectionService,
  describeEntity,
  transactionLinkId,
} from '@helios/comments'
import { CommentaryEntityContext } from '@helios/common-api'
import { Store } from '@ngrx/store'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'

/**
 * The `detail` shape of the `commentary-suspense-open` DOM CustomEvent the
 * `<smart-suspense>` web component dispatches on `document`. Frozen contract —
 * MUST match the smart-suspense-ui dispatcher exactly.
 *
 * `transactionIds` carries the origTranId of every row in `suspenseIds` that
 * is a real, persisted transaction — a distinct value from the row's opaque
 * `suspenseId` handle. Temp (not-yet-persisted) rows have none. Normalized
 * through `transactionLinkId()` before use, same as every other commentary
 * writer. Only a single-row open can link it: `CommentaryEntityContext` has
 * no bulk `transactionIds` field, mirroring the existing scalar-only
 * `transactionId` used by other panels (e.g. clearwater-transactions, breaks).
 */
interface SuspenseOpenDetail {
  accountId: string
  action: 'view' | 'add'
  suspenseIds: string[]
  transactionIds?: string[]
  /** Description for the `suspense:<id>` chip. */
  label?: string
  /**
   * Description for the `tran:<id>` chip. Composed in smart-suspense-ui, not
   * here — a deliberate exception to every other panel's pattern of building
   * this key via `clearwaterTransactionDescription()` on this side, because
   * smart-suspense-ui cannot depend on `@helios/comments`. MUST track
   * `clearwaterTransactionDescription`'s template by hand; drift between the
   * two is accepted, not guarded against.
   */
  transactionLabel?: string
}

/**
 * The `detail` shape of the `commentary-suspense-sources` DOM CustomEvent the
 * `<smart-suspense>` web component dispatches on `document` to publish its
 * currently loaded + selected rows as commentary link sources. Frozen
 * contract — MUST match the smart-suspense-ui dispatcher exactly.
 *
 * `label` rides along for future friendly-description enrichment (DLITE-3908);
 * today only `id` survives into the `CommentaryEntityRef` corpus.
 */
interface SuspenseSourcesDetail {
  loaded: { id: string; label: string }[]
  selected: { id: string; label: string }[]
}

/** DOM event-name constants — single source of truth for the listener side. */
const SUSPENSE_OPEN_EVENT = 'commentary-suspense-open' as const
const SUSPENSE_SOURCES_EVENT = 'commentary-suspense-sources' as const

/*
 * Smart Suspense is a React application served as a web component
 * Due to this, some angular standards are not followed
 * */
@Component({
  selector: 'hs-smart-suspense',
  templateUrl: './smart-suspense-panel.component.html',
  styleUrls: ['./smart-suspense-panel.component.scss'],
})
export class SmartSuspensePanelComponent extends BasePanelComponent {
  panelKey = SmartSuspenseKey
  private readonly _destroyed$ = new Subject<void>()
  source = 'helios'
  account?: MyAccountInfo

  /* ----------------------------------------------------------------------- *
   * DLITE-3911 — Smart Suspense ↔ commentary bridge.
   *
   * Smart Suspense is NOT a native AG-Grid panel: the React web component owns
   * its own comments column, context menu, sources publishing, and row focus.
   * Helios stays the single owner of the comment drawer and the entity-link
   * aggregation, so this component is the thin bridge between the SS web
   * component's DOM events / props and the shared Helios commentary services —
   * mirroring how a native panel uses those services, but sourcing from DOM
   * events instead of an AG-Grid api.
   * ----------------------------------------------------------------------- */

  /**
   * Forwarded to `<smart-suspense>` as the `commentaryAccess` input, bound as
   * the upgrade-safe `[attr.commentary-access]` attribute (a plain `[property]`
   * binding races the r2wc upgrade when the SS bundle loads async and is dropped
   * before React mounts). `'true'` enables the SS comments column + context
   * menu; anything else leaves the SS component inert. Set from the access
   * selector in ngOnInit; defaults inert until the policy resolves.
   */
  commentaryAccess = 'false'

  /**
   * Forwarded to `<smart-suspense>` as the `commentaryFocus` input. A fresh
   * object identity with an incrementing `nonce` is required so r2wc forwards
   * the change to React and repeated follows of the SAME row re-trigger the
   * focus (a value-equal object would be dropped).
   */
  commentaryFocus: { suspenseId: string; nonce: number } | null = null

  /** Whether commentary access has been granted — gates every bridge handler. */
  private hasAccessCommentary = false

  /**
   * Latest loaded / selected suspense rows the SS component published via
   * `commentary-suspense-sources`. Registered with the shared search /
   * selection services so the drawer's entity picker can offer suspense links
   * and read the current SS selection, exactly as native grids do.
   */
  private latestLoaded: CommentaryEntityRef[] = []
  private latestSelected: CommentaryEntityRef[] = []

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    readonly savedViewService: WorkspaceGridSavedViewService,
    readonly filterGroupService: FilterGroupService,
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
    private readonly followService: CommentaryFollowService,
  ) {
    super(container)
  }

  ngOnDestroy(): void {
    // Tear down the bridge: drop the source registrations and DOM listeners so
    // a closed/re-opened panel doesn't leak stale sources or duplicate handlers.
    this.entitySearchService.unregister(this.panelKey)
    this.gridSelectionService.unregister(this.panelKey)
    document.removeEventListener(
      SUSPENSE_SOURCES_EVENT,
      this.onSuspenseSources as EventListener,
    )
    document.removeEventListener(
      SUSPENSE_OPEN_EVENT,
      this.onSuspenseOpen as EventListener,
    )

    this._destroyed$.next()
    this._destroyed$.complete()
  }

  ngOnInit() {
    this.store
      .select(selectFocusedMyAccount)
      .pipe(
        debounceTime(500),
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
      )
      .subscribe(account => {
        this.account = account
      })

    // Commentary access → flips the `commentaryAccess` prop. The SS component
    // is inert without `'true'`, and the Helios-side handlers below are also
    // gated on this flag, so a no-access user gets nothing on either side.
    this.store
      .select(selectUserPolicy)
      .pipe(takeUntil(this._destroyed$))
      .subscribe(policy => {
        // Per-client gate: the accessCommentary entitlement alone. A no-access
        // user gets nothing on either side, and the backend still enforces real
        // per-account auth on every call.
        this.hasAccessCommentary = userHasCommentaryAccess(policy)
        this.commentaryAccess = this.hasAccessCommentary ? 'true' : 'false'
      })

    // Register this panel's loaded/selected suspense rows as drawer sources.
    // The getters return the latest published refs; the services re-invoke them
    // on demand, so they always reflect the SS component's current state.
    this.entitySearchService.register(this.panelKey, () => this.latestLoaded)
    this.gridSelectionService.register(this.panelKey, () => this.latestSelected)

    // Listen for the SS component's outbound DOM events (dispatched on
    // `document`, bubbles + composed so they cross the web component's Shadow
    // DOM boundary). Bound handlers give stable identity for removeEventListener.
    document.addEventListener(
      SUSPENSE_SOURCES_EVENT,
      this.onSuspenseSources as EventListener,
    )
    document.addEventListener(
      SUSPENSE_OPEN_EVENT,
      this.onSuspenseOpen as EventListener,
    )

    // Follow a suspense chip → focus the SS row. start() is idempotent (the
    // root singleton wires its listener once).
    this.followService.start()
    this.followService.follow$
      .pipe(takeUntil(this._destroyed$))
      .subscribe(target => {
        if (!this.hasAccessCommentary) return
        if (target.type !== 'suspense') return
        // New object identity + incrementing nonce so r2wc forwards the change
        // and following the SAME row twice still re-triggers focus.
        this.commentaryFocus = {
          suspenseId: target.id,
          nonce: (this.commentaryFocus?.nonce ?? 0) + 1,
        }
      })
  }

  /**
   * `commentary-suspense-sources` handler — maps the SS component's published
   * loaded/selected `{id,label}[]` to `CommentaryEntityRef[]` of
   * `{ type: 'suspense', id, label }`. The friendly `label` (DLITE-3911) rides
   * through the search/selection services so commentary-ui can render it on the
   * chip; the `id` stays the stable jump/lookup key. Bound arrow → stable
   * identity for removal.
   */
  private readonly onSuspenseSources = (
    event: CustomEvent<SuspenseSourcesDetail>,
  ): void => {
    if (!this.hasAccessCommentary) return
    const detail = event.detail
    if (detail == null) return
    this.latestLoaded = (detail.loaded ?? []).map(({ id, label }) => ({
      type: 'suspense' as const,
      id,
      label,
    }))
    this.latestSelected = (detail.selected ?? []).map(({ id, label }) => ({
      type: 'suspense' as const,
      id,
      label,
    }))
  }

  /**
   * `commentary-suspense-open` handler — opens the Helios comment drawer scoped
   * to the suspense row(s). A single selected row populates `suspenseId` (drives
   * scope + create); a bulk selection populates `suspenseIds` (drives the
   * multi-link create). A single row that is also a real transaction additionally
   * populates the distinct `transactionId` link — bulk opens can't, since
   * `CommentaryEntityContext` has no bulk transaction field.
   * Bound arrow → stable identity for removal.
   */
  private readonly onSuspenseOpen = (
    event: CustomEvent<SuspenseOpenDetail>,
  ): void => {
    if (!this.hasAccessCommentary) return
    const detail = event.detail
    if (detail == null) return
    const suspenseIds = detail.suspenseIds ?? []
    const transactionIds = detail.transactionIds ?? []
    const transactionId =
      suspenseIds.length === 1 && transactionIds.length === 1
        ? transactionLinkId(transactionIds[0])
        : undefined

    // Friendly chip labels: build a `suspense:<id>` → label map from the labels
    // the bridge already holds (latestSelected wins over latestLoaded for the
    // same id), falling back to the open event's detail.label. Mirrors the
    // native grids' DLITE-3908 entityDescriptions contract so suspense chips
    // render a label instead of a raw id. buildEntityDescriptions drops blank
    // labels and returns undefined when none resolve (no regression).
    const labelById = new Map<string, string>()
    for (const ref of [...this.latestLoaded, ...this.latestSelected]) {
      if (ref.label) labelById.set(ref.id, ref.label)
    }
    const entityDescriptions = buildEntityDescriptions([
      ...suspenseIds.map(id => {
        const label = labelById.get(id) ?? detail.label
        return label ? { key: `suspense:${id}`, label } : null
      }),
      transactionId && detail.transactionLabel
        ? describeEntity('tran', transactionId, detail.transactionLabel)
        : null,
    ])

    const entityContext: CommentaryEntityContext = {
      accountId: detail.accountId,
      suspenseId: suspenseIds.length === 1 ? suspenseIds[0] : undefined,
      suspenseIds,
      transactionId,
      entityDescriptions,
    }
    this.store.dispatch(
      openCommentaryDrawer({ entityContext, commentaryAction: detail.action }),
    )
  }

  //Normally this is an @Output event, but due to the underlying web component being a React app, we use a function
  setComponentState = (newState: JsonValue) => {
    this.setState(newState)
  }
}
